import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  Text,
  Button,
} from '@chakra-ui/react';
import { FiCamera, FiEye } from 'react-icons/fi';
import PageHeader from './shared/PageHeader';
import PerformChecklist from './PerformChecklist';
import ChecklistMonitoring from './ChecklistMonitoring';

const PreDepartureChecklist: React.FC = () => {
  const navigate = useNavigate();
  
  // Selection mode state
  const [selectionMode, setSelectionMode] = useState<'selection' | 'perform' | 'monitor'>('selection');

  // Handle selection
  const handlePerformCheck = () => {
    setSelectionMode('perform');
  };

  const handleChecklistMonitoring = () => {
    setSelectionMode('monitor');
  };

  const handleBackToSelection = () => {
    setSelectionMode('selection');
  };

  // If in selection mode, show the choice screen
  if (selectionMode === 'selection') {
    return (
      <Box minH="100vh" bgGradient="linear(135deg, blue.50, purple.50, pink.50)" w="100%" overflowX="hidden">
        <PageHeader
          title="Pre-Departure Checklist"
          subtitle="Vehicle safety check before departure"
          icon="🚗"
          onBack={() => navigate('/dashboard')}
        />

        {/* Selection Content */}
        <Container maxW="6xl" py={12} px={4}>
          <VStack spacing={12} align="stretch">

            {/* Action Buttons */}
            <VStack spacing={8} align="stretch">
              <Box
                bg="rgba(255, 255, 255, 0.8)"
                backdropFilter="blur(20px)"
                borderRadius="2xl"
                p={2}
                boxShadow="0 8px 32px rgba(0,0,0,0.12)"
                border="1px solid"
                borderColor="whiteAlpha.500"
                _hover={{
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 48px rgba(59, 130, 246, 0.2)"
                }}
                transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                cursor="pointer"
                onClick={handlePerformCheck}
              >
                <Button
                  size="xl"
                  h="100%"
                  w="100%"
                  colorScheme="blue"
                  variant="ghost"
                  borderRadius="xl"
                  _hover={{ bg: "transparent" }}
                  
                >
                  <VStack spacing={4}>
                    <Box 
                      fontSize="4xl"
                      p={4}
                      borderRadius="xl"
                      bgGradient="linear(135deg, blue.400, blue.600)"
                      color="white"
                      boxShadow="0 4px 20px rgba(59, 130, 246, 0.4)"
                    >
                      <FiCamera />
                    </Box>
                    <VStack spacing={2}>
                      <Text 
                        fontSize="xl" 
                        fontWeight="700"
                        bgGradient="linear(135deg, blue.600, blue.800)"
                        bgClip="text"
                      >
                        Perform Check
                      </Text>
                      <Text fontSize="md"
                          color="gray.600"
                          textAlign="center"
                          fontWeight="500"
                          whiteSpace="normal"
                          wordBreak="break-word"
                        >
                          Start a new pre-departure inspection checklist
                      </Text>
                    </VStack>
                  </VStack>
                </Button>
              </Box>

              <Box
                bg="rgba(255, 255, 255, 0.8)"
                backdropFilter="blur(20px)"
                borderRadius="2xl"
                p={2}
                boxShadow="0 8px 32px rgba(0,0,0,0.12)"
                border="1px solid"
                borderColor="whiteAlpha.500"
                _hover={{
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 48px rgba(34, 197, 94, 0.2)"
                }}
                transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                cursor="pointer"
                onClick={handleChecklistMonitoring}
              >
                <Button
                  size="xl"
                  h="100%"
                  w="100%"
                  colorScheme="green"
                  variant="ghost"
                  borderRadius="xl"
                  _hover={{ bg: "transparent" }}
                >
                  <VStack spacing={4}>
                    <Box 
                      fontSize="4xl"
                      p={4}
                      borderRadius="xl"
                      bgGradient="linear(135deg, green.400, green.600)"
                      color="white"
                      boxShadow="0 4px 20px rgba(34, 197, 94, 0.4)"
                    >
                      <FiEye />
                    </Box>
                    <VStack spacing={2}>
                      <Text 
                        fontSize="xl" 
                        fontWeight="700"
                        bgGradient="linear(135deg, green.600, green.800)"
                        bgClip="text"
                      >
                        Checklist Monitoring
                      </Text>
                      <Text fontSize="md" color="gray.600" textAlign="center" fontWeight="500">
                        View and monitor existing checklists
                      </Text>
                    </VStack>
                  </VStack>
                </Button>
              </Box>
            </VStack>
          </VStack>
        </Container>
      </Box>
    );
  }

  // Show Checklist Monitoring
  if (selectionMode === 'monitor') {
    return <ChecklistMonitoring onBack={handleBackToSelection} />;
  }

  // Show Perform Checklist
  return <PerformChecklist onBack={handleBackToSelection} />;
};

export default PreDepartureChecklist;
